import { Children, FC, ReactElement, useCallback, useEffect, useState, useRef, SyntheticEvent } from 'react'
import { v4 as uuidv4 } from 'uuid';
import { api, useApi } from '../../api/api';
import { ApiFileChunk } from '../../api/io';
import { Event as ApiEvent, eventFields, EventField, EventFilter, EventSorting } from '../../api/events';
import Table, { ButtonCell, TextCell, Cell } from '../../components/Table';
import FilterSettings from './FilterSettings';
import FieldSettings from './FieldSettings';
import { Accordion, Container, Form, Modal, Button, Segment, Loader, Dimmer, PaginationProps, Popup, Icon } from 'semantic-ui-react';
import FileChunkWindow from '../../components/FileChunkWindow';
import FileSizeExceedsLimitWindow from '../../components/FileSizeExceedsLimitWindow';
import InfoAlert from '../../components/InfoAlert';
import MessageWindow from '../../components/MessageWindow';
import { SyncButton, FilterButton, InjectionButton, DeleteEventsButton, DownloadArchiveButton, DownloadEventsButton } from '../../components/buttons';
import Pagination from '../../components/Pagination';
import SqlFiltersWindow from '../../components/SqlFiltersWindow'
import InjectionEventWindow from "../../components/InjectionEventWindow";
import DeleteEventsWindow from '../../components/DeleteEventsWindow'
import { envColor, confirmationWindowByEnvs } from '../../common';
import DownloadArchiveWindow from "../../components/DownloadArchiveWindow";


const PAGE_SIZE = 15;
const EXCLUDE_HEARTBEATS = {
    field: 'source' as EventField,
    operator: '!=',
    value: 'sparrow/RCG/HeartBeatWebSite'
};


const Events: FC<{}> = () => {
    const bucketApi = useApi(api.bucket);
    const eventsApi = useApi(api.events);
    const historyApi = useApi(api.history);
    const sqlApi = useApi(api.sqlFilters)

    const [filtersModal, setFiltersModal] = useState(false);
    const [injectionModal, setInjectionModal] = useState(false);
    const [deleteEventsModal, setDeleteEventsModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fileLoading, setFileLoading] = useState(false);
    const [form, setForm] = useState(false);
    const [events, setEvents] = useState<ApiEvent[]>([]);
    const [redoneEvents, setRedoneEvents] = useState<string[]>([]);
    const [activePage, setActivePageImpl] = useState<number>(1);
    const activePageRef = useRef<number>(activePage);
    const [totalPages, setTotalPages] = useState(1);
    const filterRef = useRef<EventFilter>([]);
    const [filter, setFilterImpl] = useState<EventFilter>(filterRef.current);
    const [filterOptions, setFilterOptions] = useState<EventFilter>([]);
    const sortRef = useRef<EventSorting>({field: 'process_time', operator: '>'});
    const [sort, setSortImpl] = useState<EventSorting>(sortRef.current);

    const [activeCells, setActiveCells] = useState(
        new Set<EventField>(eventFields.slice(0, 9))
    );

    const [currentFileChunk, setFileChunk] = useState<ApiFileChunk>();
    const [currentAlert, setAlert] = useState('');
    const [fileSizeExceedsLimitWindow, setFileSizeExceedsLimitWindow] = useState<{url: string, index: number}>()

    const [alertVisible, setAlertVisible] = useState(false);
    const [downloadArchiveModal, setDownloadArchiveModal] = useState(false);
    const archiveKey = useRef<string>('')

    const setActivePage = (newPage: number) => {
        activePageRef.current = newPage;
        setActivePageImpl(newPage);
    };

    const setFilter = (newFilter: EventFilter) => {
        filterRef.current = newFilter;
        setFilterImpl(newFilter);
        setActivePage(1);
    };

    const setSorting = (sort: EventSorting) => {
        sortRef.current = sort;
        setSortImpl(sort);
        setActivePage(1);
    };


    const load = async () => {
        setLoading(true);

        const idSelected = filterRef.current.findIndex((v) => v.field == "objectId") !== -1;
        const heartbeatsSelected = filterRef.current.findIndex((v) => v.value == EXCLUDE_HEARTBEATS.value) !== -1;
        const excludeHeartbeats = !(idSelected || heartbeatsSelected);

        await eventsApi
            .index(
                filterRef.current.concat(excludeHeartbeats ? [EXCLUDE_HEARTBEATS] : []),
                activePageRef.current,
                PAGE_SIZE,
                sortRef.current,
            )
            .then(({ items, count }) => {
                setEvents(items);
                setTotalPages(Math.ceil(count / PAGE_SIZE));
            })
            .catch((e) => console.error(e));

        await historyApi
            .getAllRedoneEvents()
            .then((items) => setRedoneEvents(items))
            .catch((e) => console.error(e))

        setLoading(false);
    };

    const download_events = async () => {
        setLoading(true);

        const idSelected = filterRef.current
            .findIndex(v => v.field == 'objectId') !== -1;
        const heartbeatsSelected = filterRef.current
            .findIndex(v => v.value == EXCLUDE_HEARTBEATS.value) !== -1;
        const excludeHeartbeats = !(idSelected || heartbeatsSelected);

        return eventsApi
            .index_date_range(
                filterRef.current.concat(excludeHeartbeats ? [EXCLUDE_HEARTBEATS] : []),
            )
            .then(({ items }) => {
                console.log(items)
                const blob = new Blob([atob(items)], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);

                const a = document.createElement('a');
                a.href = url;
                a.download = 'data.csv';
                document.body.appendChild(a);
                a.click();

                URL.revokeObjectURL(url);
                document.body.removeChild(a);
            })
            .catch(e => console.error(e))
            .finally(() => setLoading(false));
    };

    const repeat = async (e: ApiEvent) => {
        const confirmationResult = confirmationWindowByEnvs(`Please confirm "Rerun" action`, ['prod', 'ppe']);
        if (confirmationResult == true || confirmationResult == undefined) {
            setLoading(true);
            let clear_cache = 'false';
            await eventsApi.setProcessing(e);
            await eventsApi.repeat(e, clear_cache);
            load();
        }
    };

    const repeat_clear_cache = async (e: ApiEvent) => {
        if (window.confirm(`"Force Rerun" will bypass all validation inside SoftFilter. stage.redo will be set to True. Click OK to continue.`) == true) {
            setLoading(true);
            let clear_cache = 'true';
            await eventsApi.repeat(e, clear_cache);
            load();
        }
    };

    useEffect(() => { load() }, [filter, sort, activePage]);

    const bucketDownload = (url: string, index: number = 1) => {
        if (url == null || url === "None") {
            setAlert("Requested resource doesn't exist");
        }
        else {
            setFileLoading(true);
            bucketApi
                .read_chunk(url, index)
                .then(setFileChunk)
                .catch(() => { setAlert(`Requested resource doesn't exist : "${url}"`); })
                .finally(() => setFileLoading(false))
        }
    };

    const validateFileSize = (url: string, index: number = 1) => {
        if (url == null || url === "None") {
            setAlert("Requested resource doesn't exist");
        }
        else {
            setFileLoading(true);
            bucketApi
                .validateFileSize(url)
                .then((response) => {
                    if (response) {
                        setFileSizeExceedsLimitWindow({url, index})
                        setFileLoading(false)
                    } else {
                        bucketDownload(url, index)
                    }
                })
                .catch(() => { setAlert(`Requested resource doesn't exist : "${url}"`); })
        }
    }

    const urlDownload = (url: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', url);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
    };

    const sortByColumn = (sortField: EventField) => {
        if (sortField === 'res' && sortRef?.current?.field !== sortField) {
            setSorting({field: sortField, operator: 'errors'});
        } else if (sortRef?.current?.operator === 'errors' || sortRef?.current?.field !== sortField) {
            setSorting({field: sortField, operator: '>'});
        } else if (sortRef.current.field === sortField && sortRef.current.operator === '>') {
            setSorting({field: sortField, operator: '<'});
        } else {
            setSorting(undefined);
        }
    };

    const setSqlMenu = async () => {
        if (filtersModal){
            setFiltersModal(false)
        }
        else{
            setFiltersModal(true)
        }
    }

    const setInjectionModalHandler = async () => {
        if (injectionModal){
            setInjectionModal(false)
            await load()
        }
        else{
            setInjectionModal(true)
        }
    }

    const setDeleteEventsMenu = async () => {
        if (deleteEventsModal){
            setDeleteEventsModal(false);
        }
        else{
            setDeleteEventsModal(true);
        }
    }

    const closeFileSizeExceedsLimitWindow = async () => {
        setFileSizeExceedsLimitWindow(undefined)
        setFileLoading(false)
    }

    const closeFileChunkWindow = async () => {
        closeFileSizeExceedsLimitWindow()
        setFileChunk(undefined)
    }

    const setDownloadArchiveModalHandler = async () => {
        if (downloadArchiveModal){
            setDownloadArchiveModal(false)
            await load()
        }
        else{
            setDownloadArchiveModal(true)
        }
    }

    const downloadArchiveHandler = async (date: Date[]) => {
        try {
            const source = filterRef.current.filter(item => item.field === 'source' && item.operator === '=')[0].value;
            const uuid = uuidv4();
            const dateString = date.map(item => item.toLocaleDateString())
            bucketApi.generateArchive(uuid, dateString, source).catch(() => console.log('Archive generating'));
            archiveKey.current = uuid
            load()
            setAlertVisible(true);
        } catch (error) {
            setAlert(`Requested resource doesn't exist`);
        }
    };

    const readyToDownloadArchive = async (url: string) => {
        urlDownload(url)
        setAlertVisible(false)
    };

    useEffect(() => {
        eventsApi.filters().then(setFilterOptions);
    }, []);

    const menu = [
        {
            key: 0,
            title: 'Field settings',
            content: {
                content: (
                    <FieldSettings fields={eventFields} visibleFields={activeCells} onChange={setActiveCells} />
                )
            }
        }
    ];

    const onFormRestore = (value?: any) => {
        let textField = document.getElementById("objectId") as HTMLInputElement;
        if (textField !== null ) {
            let searchBox = 'source LIKE %'+value + '%'
            setLoading(true);

            if (filtersModal){
                textField.value = value
                setSqlMenu()
            }
            else{
                textField.value = searchBox;
            }
            textField.click()
            setLoading(false);
        }
    }

    const getTableCells = useCallback(() => {
        const text = (header: string, value: (data: ApiEvent) => any) => (
            <TextCell {...{header, value}} />
        );

        const sortTextCell = (header: string, value: (data: ApiEvent) => any, sortField: EventField, textStyle: object = {}) => (
            <TextCell header={header}
                      textStyle={textStyle}
                      headerOnClick={() => sortByColumn(sortField)}
                      value={value}/>
        )

        const popupForSource = (source: string | undefined, source_file: string | undefined) => {
            if (source && source_file) {
                let popupValue = source;

                if (source_file.includes('sparrow/RCG/')){
                    // source_file name pattern:
                    // https://{API_ENDPOINT}/raw_data/sparrow/RCG/{source_path}/{datetime}/{file_name}
                    const source_parts = source_file.split('sparrow/RCG/')[1].split("/");
                    popupValue = source_parts.slice(0, source_parts.length - 2).join("/");
                }
                let paragraph =
                <p
                    onClick={() => onFormRestore(popupValue)}
                    style={{display: 'inline'}}>
                    {source ?? 'null'}
                </p>;
                return <Popup content={popupValue} trigger={paragraph}/>
            }
        }

        const popupForResWithError = (cellValue: string | undefined, popUpValue: string | undefined) => {
            if (popUpValue === null) {
                return <p style={{display: 'inline'}}>{cellValue}</p>;
            } else {
                const paragraph = (
                    <div>
                        <p style={{display: 'inline'}}>{cellValue}</p>
                        <Icon name="info circle"/>
                    </div>
                );
                return <Popup content={popUpValue} trigger={paragraph}/>
            }
        };

        const textCellWithPopUpForFileUrl = (header: string, fileUrlValue: (data: ApiEvent) => any) => (
            <TextCell header={header} value={(e) => popupForFileUrl(fileUrlValue(e))}/>
        )

        const popupForFileUrl = (fileUrlValue: string) => {
            let popupValue = fileUrlValue.split("/").slice(-1)[0];
            let button = (
                <ButtonCell
                    style={{ display: "inline", background: "white", borderTop: "rgba(0,0,0,.0)" }}
                    header=""
                    value={() => fileUrlValue}
                    icon="arrow down"
                    onClick={fileUrlValue.endsWith(".zip") ? urlDownload : validateFileSize}
                    color={envColor}
                />
            );
            if (popupValue === "None") {
                button = (
                    <ButtonCell
                        disabled
                        button={{ color: "grey" }}
                        style={{ display: "inline", background: "white", borderTop: "rgba(0,0,0,.0)" }}
                        header=""
                        value={() => ""}
                        icon="arrow down"
                        color={envColor}
                    />
                );
            }
            return <Popup content={popupValue} trigger={button} />;
        };

        const cellDefinitions: Record<EventField, ReactElement> = {
            objectId:           text('id', e => e.objectId),
            source:             sortTextCell('source', e => popupForSource(e.source, e.source_file), 'source', { cursor: 'pointer' }),
            download_end:       sortTextCell('download end', e => e.download_end, 'download_end'),
            process_time:       sortTextCell('process time', e => e.process_time, 'process_time'),
            source_file:        textCellWithPopUpForFileUrl('input', e => e.source_file),
            json_output:        textCellWithPopUpForFileUrl('output', e => e.json_output),
            cfg:                textCellWithPopUpForFileUrl('config', e => e.cfg),
            event_log:          textCellWithPopUpForFileUrl('event log', e => e.event_log),
            process_log:        textCellWithPopUpForFileUrl('process log', e => e.process_log),
            download_start:     text('download_start', e => e.download_start),
            file_size:          text('file size', e => e.file_size),
            res:                sortTextCell('result', e => e.error_category !== null ? popupForResWithError(e.error_category, e.error_message) : e.res, 'res'),
            source_update_time: text('source update time', e => e.source_update_time),
            upload_S3_time:     text('s3 update time', e => e.source_update_time),
            error_category:     text('error category', e => e.error_category),
            error_message:      text('error message', e => e.error_message),
        };

        const cells = Object
            .keys(cellDefinitions)
            .filter(key => activeCells.has(key as EventField))
            .map(key => cellDefinitions[key as EventField]);


        const popupForClearRedo = (e: ApiEvent) => {
            let button_force = (
                <ButtonCell
                    header=''
                    icon='redo alternate'
                    style={{borderTop: "rgba(0,0,0,.0)", borderRight: "rgba(0,0,0,.0)", borderLeft: "rgba(0,0,0,.0)"}}
                    button={{ basic: false }}
                    value={() => e}
                    onClick={repeat_clear_cache}
                    color={undefined}
                />
            );


            let button = (
                <ButtonCell
                    header=''
                    icon='redo'
                    style={{borderTop: "rgba(0,0,0,.0)", borderRight: "rgba(0,0,0,.0)", borderLeft: "rgba(0,0,0,.0)"}}
                    button={{ basic: false }}
                    value={() => e}
                    onClick={repeat}
                    color={envColor}
                />
            );

            return (
                <div style={{display: "block"}}>
                    <Popup content='Rerun' trigger={button}/>
                    <Popup content='Force Rerun' trigger={button_force}/>
                </div>
            );
        }


        const clearRedoCell = (
            <Cell
                header=""
                value={e => popupForClearRedo(e)}
                render={value => <div style={{display: 'inline-block'}}>{value ?? 'null'}</div>}
            />
        );

        return Children.toArray([clearRedoCell].concat(cells));
    }, [activeCells]);



    const onFormChange = () => {
        if (form){
            setForm(false)
        }
        else {
            setForm(true)
        }
    }

    const getHighlightText = (event: ApiEvent) => {
        let text = [];
        if (event.event_log && event.event_log.split("/").slice(-1)[0] === "None") {
            text.push("test");
        }
        if (event.objectId && redoneEvents.includes(event.objectId)) {
            text.push("redone");
        }
        if (text.length) {
            text.push("event");
        }
        return text.join(" ");
    };

    const isDownloadArchiveButtonVisible = () => {
        return filterRef.current.length > 0 &&
               events.length > 0 &&
               filterRef.current.some(item => item.field === 'source' && item.operator === '=');
    };

    const renderForm = () => {
        let sqlData = ''
        let sqlTag = ''

        const onAdd = async () => {
            setLoading(true)
            setForm(false)
            setFiltersModal(false)
            return sqlApi
            .write(
            {sql_query: sqlData, tag: sqlTag},
            )
            .then(() => {})
            .catch(e => console.error(e))
            .finally(()=>setLoading(false));
        }


        const onFormFieldChange = (type: string, change_string: any) => {
            if (type==='sql'){
                sqlData=change_string
            }
            else{
                sqlTag=change_string
            }
        }

        const form =
        <Modal
            size='large'
            style={{ minWidth: '70%', maxWidth: '95%' }}
            closeIcon
            open={true}
            onClose={onFormChange}
        >
        <Modal.Header>Add filter</Modal.Header>
        <Modal.Content scrolling>

            <Form>
            <Form.Field required>
              <label>SQL query</label>
              <input placeholder='Your SQL filter' onChange={({ target: { value } }) => onFormFieldChange('sql', value)}/>
            </Form.Field>
            <Form.Field>
              <label>Tag [Optional] </label>
              <input placeholder='Name for the filter' onChange={({ target: { value } }) => onFormFieldChange('tag', value)} />
            </Form.Field>
            <Button
            type='submit'
            onClick={onAdd}>
            Submit
            </Button>
          </Form>

        </Modal.Content>

        <Modal.Actions>
            <Button circular onClick={onFormChange}>
            Close
            </Button>
        </Modal.Actions>

        </Modal>

      return form
    }

    return (
        <div>
            <Dimmer active={fileLoading}><Loader/></Dimmer>

            {currentFileChunk &&
                <FileChunkWindow
                    file={currentFileChunk}
                    onClose={closeFileChunkWindow.bind(this)}
                    filePageChange={bucketDownload}
                    loading={fileLoading}
                    saveFile={urlDownload}
                />
            }

            {fileSizeExceedsLimitWindow &&
                <FileSizeExceedsLimitWindow
                    size="small"
                    loading={fileLoading}
                    onClose={closeFileSizeExceedsLimitWindow.bind(this)}
                    fileInfo={fileSizeExceedsLimitWindow}
                    saveFile={urlDownload}
                    showFile={bucketDownload}
                />
            }

            {currentAlert.length > 0 &&
                <MessageWindow
                    size="small"
                    message={currentAlert}
                    onClose={() => setAlert('')}
                />
            }

            {filtersModal &&
                <SqlFiltersWindow
                onRestore={onFormRestore.bind(this)}
                onClose={setSqlMenu.bind(this)}
                onForm={onFormChange.bind(this)}
                onApply={console.log}
                />
            }

            {injectionModal &&
                <InjectionEventWindow
                onClick={setInjectionModalHandler.bind(this)}
                />
            }

            {deleteEventsModal &&
                 <DeleteEventsWindow
                     filter={filterRef.current}
                     onClose={setDeleteEventsMenu.bind(this)}
                     onDeleteClicked={load.bind(this)}
                 />
              }

            {downloadArchiveModal &&
                <DownloadArchiveWindow
                    onClose={setDownloadArchiveModalHandler.bind(this)}
                    onDownload={downloadArchiveHandler.bind(this)}
                />
            }
            {alertVisible &&
                <InfoAlert message="Don't leave this page. Archive will download when processed files: " onClose={readyToDownloadArchive} archiveKey={archiveKey.current}/>
            }

            {form && renderForm()}

            <Container>
                <Segment>
                    <Accordion defaultActiveIndex={-1} panels={menu} />
                </Segment>
            </Container>

            <Segment style={{ margin: '50px 25px 0 50px' }}>
                <Container fluid>
                    <SyncButton load={load} color={envColor} />
                    <FilterButton load={setSqlMenu} color={envColor} />
                    <InjectionButton load={setInjectionModalHandler} color={envColor} />
                    <DeleteEventsButton load={setDeleteEventsMenu} color={envColor} />
                    {isDownloadArchiveButtonVisible() &&
                        <DownloadArchiveButton load={setDownloadArchiveModalHandler} color={envColor} disabled={alertVisible} />
                    }
                    <DownloadEventsButton load={download_events} color={envColor}/>

                    <FilterSettings {...{options: filterOptions, filter, onChange: setFilter}} />

                    {loading ?
                        <Dimmer inverted active={true}><Loader/></Dimmer> :
                        <>
                            <Table rows={events ?? []}
                                   highlighted={{
                                       getHighlightText: getHighlightText,
                                       highlightIndex: 1,
                                       highlightClassName: 'highlighted-event'
                                   }}
                                   sort={sort}>
                                {getTableCells()}
                            </Table>
                            <Pagination
                                activePage={activePage}
                                totalPages={totalPages}
                                onPageChange={setActivePage}
                            />
                        </>
                    }
                </Container>
            </Segment>
        </div>
    );
};


export default Events;
